/* eslint-disable @angular-eslint/directive-selector */
import {
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  IFormFieldValidation,
  ValidationType,
} from '@nibtravel/shared/util/models';
import { merge, NEVER, Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  startWith,
  takeUntil,
} from 'rxjs/operators';

import { BaseInputComponent } from '../../../common/base-input.component';
import { CustomValidator } from './custom.validators';

@Directive({
  selector: 'nibtravel-validation-handler, [validationsHandler]',
})
export class ValidationHandlerDirective implements OnInit, OnDestroy {
  destroy$ = new Subject();

  /**
   * Used to manually trigger validations as until FormControl status is changed
   * no events are emitted to trigger checkForErrors.
   */
  @Input() trigger!: Observable<void>;

  /** The array of validations for the control on which this directive is applied */
  @Input() validations!: IFormFieldValidation[];

  /** The reference of the form control */
  @Input() control!: AbstractControl;

  /** A reference of the base input component. */
  @Input() host!: BaseInputComponent;

  /** When value is true, the validation will only be triggered if the change value of control is different than the original value. */
  @Input() enableDistinctUntilChanged = true;

  /**
   * A simple event to notify when a validation has been changed.
   * It emits an array of errors (string), if exists or empty array.
   */
  @Output() validationResult = new EventEmitter<string[]>();

  constructor(private cd: ChangeDetectorRef) {}

  /** Wraps the initialization of the directive, builds the validators and subscribe for changes */
  ngOnInit(): void {
    if (this.control && this.validations) {
      const validators = this.buildValidators(this.control);
      this.control.setValidators(validators);

      const asyncValidators = this.buildAsyncValidators(this.control);
      this.control.setAsyncValidators(asyncValidators);

      if (
        this.control instanceof FormArray ||
        this.control instanceof FormGroup
      ) {
        this.control.updateValueAndValidity({ onlySelf: true });
        this.checkForError();
      } else if (this.control.value) {
        this.control.markAsDirty();
        this.control.updateValueAndValidity();
        this.checkForError();
      }

      let valueChanges$ = this.control?.valueChanges ?? NEVER;
      if (this.enableDistinctUntilChanged) {
        valueChanges$ = valueChanges$.pipe(distinctUntilChanged());
      }

      let statusChanges$ = this.control?.statusChanges ?? NEVER;
      if (this.enableDistinctUntilChanged) {
        statusChanges$ = statusChanges$.pipe(distinctUntilChanged());
      }

      merge(valueChanges$, statusChanges$, this.trigger ?? NEVER)
        .pipe(
          startWith(this.control.status),
          debounceTime(250),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.checkForError();
          this.cd.markForCheck();
        });
    }
  }

  /** Default implementation to unsubscribe the observables using the takeUntil pattern */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Build the array of validators */
  buildValidators(
    control: AbstractControl
  ): ValidatorFn | ValidatorFn[] | null {
    return this.validations
      ? this.validations
          .filter((validation) => !validation.async)
          .map((validation) => this.getValidator(control, validation))
      : null;
  }

  /**
   * Build the array of async validators, this block is untested!!
   * Need to come back once we do have async validators in place
   */
  buildAsyncValidators(
    control: AbstractControl
  ): AsyncValidatorFn | AsyncValidatorFn[] | null {
    const validators: AsyncValidatorFn[] = [];
    this.validations
      .filter((validation) => validation.async)
      .forEach((validation) => {
        const validator = validation.custom?.validator(control);
        if (validator) {
          validators.push(validator as AsyncValidatorFn);
        }
      });

    return validators;
  }

  /** Simple abstraction to get the correct error key */
  getErrorKey(validation: IFormFieldValidation): string {
    if (validation.type === ValidationType.Custom && validation.custom) {
      return validation.custom.name;
    } else if (validation.type === ValidationType.RequiredTrue) {
      return 'required';
    } else {
      return validation.type;
    }
  }

  /**
   * Checks whether this ngControl has error and the field is not on initial state.
   * It also emits validation result event, which is trigger regardless the result.
   */
  checkForError(): void {
    const errors: string[] = [];
    this.validations.forEach((validation) => {
      const key = this.getErrorKey(validation);
      const hasError = this.control.hasError(key) && this.control.touched;

      let msg: string | undefined;
      if (hasError) {
        msg = this.errorMessageHandler(validation);
        if (msg) {
          errors.push(msg);
        }
      }
    });

    this.validationResult.emit(errors);
  }

  /** The error messages are designed to use response values from the validators */
  errorMessageHandler(validation: IFormFieldValidation): string | undefined {
    if (this.control) {
      const key = this.getErrorKey(validation);
      const error = this.control.getError(key);

      return validation.message
        ? validation.message(this.control, error, this.host, validation)
        : error;
    }

    return undefined;
  }

  private getValidator(
    control: AbstractControl,
    validation: IFormFieldValidation
  ): any {
    switch (validation.type) {
      case ValidationType.Min:
        return Validators.min(validation.parameter);
      case ValidationType.Max:
        return Validators.max(validation.parameter);
      case ValidationType.MinLength:
        return Validators.minLength(validation.parameter);
      case ValidationType.MaxLength:
        return Validators.maxLength(validation.parameter);
      case ValidationType.Required:
        return CustomValidator.required();
      case ValidationType.RequiredTrue:
        return Validators.requiredTrue;
      case ValidationType.Email:
        return CustomValidator.email();
      case ValidationType.Pattern:
        return Validators.pattern(validation.parameter);
      case ValidationType.Custom:
        return validation.custom?.validator(control);
      default:
        return undefined;
    }
  }
}
